import { useState, useEffect } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Switch,
  Popconfirm,
  message,
  Card,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Tooltip,
  Row,
  Col,
  Statistic,
  Avatar,
  Typography,
  Upload,
  DatePicker,
  Tabs,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  InboxOutlined,
  UserOutlined,
  ToolOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "../../api/axios";

const { Text } = Typography;
const { Option } = Select;

// مطابق لـ path('api/custom_services/', include('custom_services.urls')) في urls.py الرئيسي
const API_BASE = "/custom_services/admin/messages/";

const CLOUD_NAME = "dftpzis0y";
const UPLOAD_PRESET = "ml_default";

const AUDIENCE_LABELS = { customer: "عميل", provider: "فني" };
const AUDIENCE_COLORS = { customer: "orange", provider: "blue" };

export default function Messages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [audienceTab, setAudienceTab] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const fetchMessages = async (audience = audienceTab) => {
    setLoading(true);
    try {
      const params = audience !== "all" ? `?audience=${audience}` : "";
      const res = await api.get(`${API_BASE}${params}`);
      const data = Array.isArray(res.data) ? res.data : res.data.results ?? [];
      setMessages(data);
    } catch {
      message.error("فشل تحميل الرسائل");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages(audienceTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceTab]);

  const handleImageUpload = async ({ file }) => {
    if (file.size > 5 * 1024 * 1024) {
      message.error("حجم الصورة يجب أن يكون أقل من 5MB");
      return false;
    }

    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("upload_preset", UPLOAD_PRESET);
      fd.append("folder", "app_messages");

      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
        { method: "POST", body: fd }
      );

      const data = await res.json();
      if (data.secure_url) {
        form.setFieldValue("image", data.secure_url);
        setPreviewImage(data.secure_url);
        message.success("تم رفع الصورة بنجاح");
      } else {
        message.error("فشل رفع الصورة، حاول مرة أخرى");
      }
    } catch {
      message.error("حدث خطأ أثناء رفع الصورة");
    } finally {
      setUploadingImage(false);
    }
    return false;
  };

  const openModal = (msg = null) => {
    setEditingMessage(msg);
    setPreviewImage(msg?.image ?? null);
    if (msg) {
      form.setFieldsValue({
        audience: msg.audience,
        title: msg.title,
        body: msg.body,
        image: msg.image,
        link: msg.link,
        priority: msg.priority,
        is_active: msg.is_active,
        date_range:
          msg.start_at || msg.end_at
            ? [
                msg.start_at ? dayjs(msg.start_at) : null,
                msg.end_at ? dayjs(msg.end_at) : null,
              ]
            : undefined,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        is_active: true,
        priority: 0,
        audience: audienceTab !== "all" ? audienceTab : "customer",
      });
      setPreviewImage(null);
    }
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const { date_range, ...rest } = values;
      const payload = {
        ...rest,
        start_at: date_range?.[0] ? date_range[0].toISOString() : null,
        end_at: date_range?.[1] ? date_range[1].toISOString() : null,
      };

      if (editingMessage) {
        await api.patch(`${API_BASE}${editingMessage.id}/`, payload);
        message.success("تم تحديث الرسالة بنجاح");
      } else {
        await api.post(API_BASE, payload);
        message.success("تم إضافة الرسالة بنجاح");
      }
      setModalOpen(false);
      fetchMessages();
    } catch (e) {
      if (e?.errorFields) return;
      message.error("فشل الحفظ، حاول مرة أخرى");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`${API_BASE}${id}/`);
      message.success("تم حذف الرسالة");
      fetchMessages();
    } catch {
      message.error("فشل الحذف");
    }
  };

  const handleToggleActive = async (id, current) => {
    try {
      await api.patch(`${API_BASE}${id}/`, { is_active: !current });
      message.success(current ? "تم تعطيل الرسالة" : "تم تفعيل الرسالة");
      fetchMessages();
    } catch {
      message.error("فشل تغيير الحالة");
    }
  };

  const activeCount = messages.filter((m) => m.is_active).length;
  const customerCount = messages.filter(
    (m) => m.audience === "customer"
  ).length;
  const providerCount = messages.filter(
    (m) => m.audience === "provider"
  ).length;

  const columns = [
    {
      title: "الرسالة",
      key: "title",
      render: (_, r) => (
        <Space>
          {r.image ? (
            <Avatar
              size={44}
              src={r.image}
              shape="square"
              style={{ borderRadius: 8, flexShrink: 0 }}
            />
          ) : (
            <Avatar
              size={44}
              icon={<MessageOutlined />}
              shape="square"
              style={{ background: "#e07b1a", borderRadius: 8, flexShrink: 0 }}
            />
          )}
          <div>
            <div style={{ fontWeight: 600, color: "#1a1a2e" }}>{r.title}</div>
            <Text
              type="secondary"
              ellipsis
              style={{ fontSize: 12, maxWidth: 260, display: "block" }}
            >
              {r.body}
            </Text>
          </div>
        </Space>
      ),
    },
    {
      title: "الفئة",
      dataIndex: "audience",
      key: "audience",
      render: (v) => (
        <Tag
          color={AUDIENCE_COLORS[v]}
          icon={v === "customer" ? <UserOutlined /> : <ToolOutlined />}
          style={{ borderRadius: 6 }}
        >
          {AUDIENCE_LABELS[v] ?? v}
        </Tag>
      ),
    },
    {
      title: "الأولوية",
      dataIndex: "priority",
      key: "priority",
      render: (v) => <Tag color="default">{v}</Tag>,
    },
    {
      title: "الرابط",
      dataIndex: "link",
      key: "link",
      render: (v) =>
        v ? (
          <a href={v} target="_blank" rel="noreferrer">
            <LinkOutlined /> فتح
          </a>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "الحالة",
      key: "is_active",
      render: (_, r) => (
        <Switch
          checked={r.is_active}
          checkedChildren="نشطة"
          unCheckedChildren="معطلة"
          onChange={() => handleToggleActive(r.id, r.is_active)}
          style={r.is_active ? { background: "#52c41a" } : {}}
        />
      ),
    },
    {
      title: "الإجراءات",
      key: "actions",
      fixed: "left",
      render: (_, r) => (
        <Space size="small">
          <Tooltip title="تعديل">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openModal(r)}
              style={{ color: "#e07b1a" }}
            />
          </Tooltip>
          <Popconfirm
            title="حذف الرسالة؟"
            description="سيتم حذف الرسالة نهائياً."
            onConfirm={() => handleDelete(r.id)}
            okText="حذف"
            cancelText="إلغاء"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="حذف">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                style={{ color: "#ff4d4f" }}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ fontFamily: "'Cairo', sans-serif" }}>
      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {[
          {
            title: "رسائل العملاء",
            value: customerCount,
            icon: <UserOutlined />,
            color: "#e07b1a",
            bg: "#fff7e6",
          },
          {
            title: "رسائل الفنيين",
            value: providerCount,
            icon: <ToolOutlined />,
            color: "#1677ff",
            bg: "#e6f4ff",
          },
          {
            title: "رسائل نشطة",
            value: activeCount,
            icon: <CheckCircleOutlined />,
            color: "#52c41a",
            bg: "#f6ffed",
          },
        ].map((s, i) => (
          <Col xs={24} sm={8} key={i}>
            <Card
              bordered={false}
              style={{ borderRadius: 12, background: s.bg }}
              bodyStyle={{ padding: "16px 20px" }}
            >
              <Statistic
                title={
                  <span style={{ color: "#666", fontSize: 13 }}>{s.title}</span>
                }
                value={s.value}
                valueStyle={{ color: s.color, fontWeight: 700, fontSize: 28 }}
                prefix={
                  <span style={{ color: s.color, marginLeft: 6 }}>
                    {s.icon}
                  </span>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>

      {/* Table Card */}
      <Card
        bordered={false}
        style={{ borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
        bodyStyle={{ padding: 0 }}
      >
        <div
          style={{
            padding: "16px 24px 0",
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: "#0f1f1a",
            }}
          >
            رسائل داخل التطبيق
          </h2>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
            style={{ background: "#e07b1a", borderColor: "#e07b1a" }}
          >
            إضافة رسالة
          </Button>
        </div>

        <Tabs
          activeKey={audienceTab}
          onChange={setAudienceTab}
          style={{ padding: "0 24px" }}
          items={[
            { key: "all", label: "الكل" },
            { key: "customer", label: "العملاء" },
            { key: "provider", label: "الفنيين" },
          ]}
        />

        <Table
          columns={columns}
          dataSource={messages}
          loading={loading}
          rowKey="id"
          scroll={{ x: 900 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showTotal: (total) => `${total} رسالة`,
          }}
          style={{ padding: "0 8px" }}
        />
      </Card>

      {/* Modal */}
      <Modal
        title={
          <span style={{ fontFamily: "'Cairo', sans-serif", fontWeight: 700 }}>
            {editingMessage ? "تعديل الرسالة" : "إضافة رسالة جديدة"}
          </span>
        }
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => {
          setModalOpen(false);
          setPreviewImage(null);
        }}
        okText={editingMessage ? "حفظ التعديلات" : "إضافة"}
        cancelText="إلغاء"
        confirmLoading={saving}
        okButtonProps={{
          style: { background: "#e07b1a", borderColor: "#e07b1a" },
          disabled: uploadingImage,
        }}
        style={{ direction: "rtl", fontFamily: "'Cairo', sans-serif" }}
        width={560}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="audience"
            label="الفئة المستهدفة"
            rules={[{ required: true, message: "اختر الفئة المستهدفة" }]}
          >
            <Select placeholder="اختر الفئة">
              <Option value="customer">
                <UserOutlined /> عملاء
              </Option>
              <Option value="provider">
                <ToolOutlined /> فنيين
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="title"
            label="عنوان الرسالة"
            rules={[{ required: true, message: "الرجاء إدخال العنوان" }]}
          >
            <Input placeholder="مثال: عرض خاص هذا الأسبوع" />
          </Form.Item>

          <Form.Item
            name="body"
            label="نص الرسالة"
            rules={[{ required: true, message: "الرجاء إدخال نص الرسالة" }]}
          >
            <Input.TextArea rows={3} placeholder="محتوى الرسالة..." />
          </Form.Item>

          {/* hidden URL field */}
          <Form.Item name="image" style={{ display: "none" }}>
            <Input />
          </Form.Item>

          <Form.Item label="صورة الرسالة (اختياري)">
            <Upload
              accept="image/*"
              showUploadList={false}
              customRequest={handleImageUpload}
              disabled={uploadingImage}
            >
              <div
                style={{
                  border: `1px dashed ${
                    uploadingImage ? "#e07b1a" : "#d9d9d9"
                  }`,
                  borderRadius: 10,
                  padding: previewImage ? 8 : 20,
                  textAlign: "center",
                  cursor: uploadingImage ? "not-allowed" : "pointer",
                  background: "#fafafa",
                  transition: "border-color 0.25s",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  if (!uploadingImage)
                    e.currentTarget.style.borderColor = "#e07b1a";
                }}
                onMouseLeave={(e) => {
                  if (!uploadingImage)
                    e.currentTarget.style.borderColor = "#d9d9d9";
                }}
              >
                {uploadingImage ? (
                  <Space direction="vertical" size={6} style={{ padding: 12 }}>
                    <LoadingOutlined
                      style={{ fontSize: 26, color: "#e07b1a" }}
                    />
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      جاري رفع الصورة...
                    </Text>
                  </Space>
                ) : previewImage ? (
                  <Space
                    direction="vertical"
                    size={8}
                    style={{ width: "100%" }}
                  >
                    <img
                      src={previewImage}
                      alt="preview"
                      style={{
                        width: "100%",
                        maxHeight: 140,
                        objectFit: "cover",
                        borderRadius: 8,
                        display: "block",
                      }}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      اضغط لتغيير الصورة
                    </Text>
                  </Space>
                ) : (
                  <Space direction="vertical" size={4}>
                    <InboxOutlined style={{ fontSize: 32, color: "#bbb" }} />
                    <Text style={{ fontSize: 14, color: "#555" }}>
                      اضغط أو اسحب الصورة هنا
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      PNG، JPG، WEBP — الحجم الأقصى 5MB
                    </Text>
                  </Space>
                )}
              </div>
            </Upload>
          </Form.Item>

          <Form.Item
            name="link"
            label="رابط عند الضغط (اختياري)"
            rules={[{ type: "url", message: "الرجاء إدخال رابط صحيح" }]}
          >
            <Input placeholder="https://..." />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="الأولوية"
                tooltip="الرقم الأعلى يظهر أولاً"
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="الحالة"
                valuePropName="checked"
              >
                <Switch checkedChildren="نشطة" unCheckedChildren="معطلة" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="date_range"
            label="فترة الظهور (اختياري)"
            tooltip="اتركها فارغة لتظهر الرسالة دائماً طالما الحالة نشطة"
          >
            <DatePicker.RangePicker
              showTime
              style={{ width: "100%" }}
              placeholder={["من", "إلى"]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
